import React, { useState, useRef, useCallback } from "react";

const ProfilePictureCropper = ({ isOpen, onClose, onCropComplete, imageSrc }) => {
  const [crop, setCrop] = useState({ x: 50, y: 50 });
  const [zoom, setZoom] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  const handleImageLoad = (e) => {
    imageRef.current = e.target;
    drawCanvas();
  };

  const drawCanvas = useCallback(() => {
    if (!canvasRef.current || !imageRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const img = imageRef.current;
    
    const containerWidth = 300;
    const containerHeight = 300;
    
    // Clear canvas
    ctx.clearRect(0, 0, containerWidth, containerHeight);
    
    // Calculate scaled dimensions
    const scale = zoom;
    const scaledWidth = img.naturalWidth * scale;
    const scaledHeight = img.naturalHeight * scale;
    
    // Calculate position (centered with crop offset)
    const x = (containerWidth - scaledWidth) / 2 + crop.x;
    const y = (containerHeight - scaledHeight) / 2 + crop.y;
    
    // Draw image
    ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
    
    // Draw overlay (darken area outside circle)
    ctx.save();
    ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
    ctx.fillRect(0, 0, containerWidth, containerHeight);
    
    // Cut out circle
    ctx.globalCompositeOperation = "destination-out";
    ctx.beginPath();
    ctx.arc(containerWidth / 2, containerHeight / 2, 120, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    
    // Draw circle border
    ctx.strokeStyle = "#3b82f6";
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(containerWidth / 2, containerHeight / 2, 120, 0, Math.PI * 2);
    ctx.stroke();
  }, [crop, zoom]);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - crop.x,
      y: e.clientY - crop.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    setCrop({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({
      x: touch.clientX - crop.x,
      y: touch.clientY - crop.y
    });
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    
    setCrop({
      x: touch.clientX - dragStart.x,
      y: touch.clientY - dragStart.y
    });
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  React.useEffect(() => {
    drawCanvas();
  }, [crop, zoom, drawCanvas]);

  const getCroppedImage = async () => {
    if (!imageRef.current) return null;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = imageRef.current;
    
    const size = 240; // Output size (diameter of circle)
    canvas.width = size;
    canvas.height = size;
    
    // Calculate crop area
    const containerWidth = 300;
    const containerHeight = 300;
    const scale = zoom;
    const scaledWidth = img.naturalWidth * scale;
    const scaledHeight = img.naturalHeight * scale;
    
    const x = (containerWidth - scaledWidth) / 2 + crop.x;
    const y = (containerHeight - scaledHeight) / 2 + crop.y;
    
    // Calculate source crop (from center of preview circle)
    const centerX = containerWidth / 2;
    const centerY = containerHeight / 2;
    const radius = 120;
    
    const sourceX = (centerX - radius - x) / scale;
    const sourceY = (centerY - radius - y) / scale;
    const sourceSize = (radius * 2) / scale;
    
    // Create circular clipping path
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();
    
    // Draw cropped image
    ctx.drawImage(
      img,
      sourceX,
      sourceY,
      sourceSize,
      sourceSize,
      0,
      0,
      size,
      size
    );
    
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, "image/jpeg", 0.9);
    });
  };

  const handleSave = async () => {
    const croppedBlob = await getCroppedImage();
    if (croppedBlob) {
      onCropComplete(croppedBlob);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        <div className="bg-blue-600 text-white p-4 rounded-t-xl">
          <h2 className="text-xl font-bold">Crop Profile Picture</h2>
          <p className="text-sm text-blue-100 mt-1">Drag to position, use slider to zoom</p>
        </div>
        
        <div className="p-6">
          {/* Canvas Container */}
          <div 
            ref={containerRef}
            className="relative bg-gray-100 rounded-lg overflow-hidden mx-auto mb-4"
            style={{ width: 300, height: 300, cursor: isDragging ? 'grabbing' : 'grab' }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <canvas 
              ref={canvasRef} 
              width={300} 
              height={300}
              className="absolute inset-0"
            />
            <img
              src={imageSrc}
              alt="Crop"
              onLoad={handleImageLoad}
              className="hidden"
            />
          </div>

          {/* Zoom Slider */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Zoom: {Math.round(zoom * 100)}%
            </label>
            <input
              type="range"
              min="0.5"
              max="3"
              step="0.1"
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${((zoom - 0.5) / 2.5) * 100}%, #dbeafe ${((zoom - 0.5) / 2.5) * 100}%, #dbeafe 100%)`
              }}
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>50%</span>
              <span>300%</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePictureCropper;
